import React, { useMemo, useState } from 'react';
import { X, TrendingUp, Target, Star, Calendar, Trophy } from './Icons';
import { useSupabaseData } from '../hooks/useSupabaseData';

const ProgressTracker = ({ 
  isOpen, 
  onClose, 
  userProgress, 
  language = 'traditional' 
}) => {
  const [activeTab, setActiveTab] = useState('overview');

  // Load user progress from Supabase if available and merge with incoming prop
  const { data: remote } = useSupabaseData({
    table: 'eng_userprogress',
    mapRow: (row) => row,
    getFallback: () => null,
    query: {
      // Ideally filter by user id if you pass it in via userProgress.userId
      // eq: userProgress?.userId ? { user_id: userProgress.userId } : undefined
    }
  });

  const mergedProgress = useMemo(() => {
    if (!remote || (Array.isArray(remote) && remote.length === 0)) return userProgress || {};
    const rows = Array.isArray(remote) ? remote : [remote];
    const base = { ...(userProgress || {}) };
    // Map common sections if present
    const latest = rows[0];
    base.statistics = base.statistics || {};
    if (latest.weekly_progress_minutes != null) base.statistics.weeklyProgress = latest.weekly_progress_minutes;
    if (latest.weekly_goal_minutes != null) base.statistics.weeklyGoal = latest.weekly_goal_minutes;
    if (latest.average_score != null) base.statistics.averageScore = latest.average_score;
    if (latest.total_words_learned != null) base.statistics.totalWordsLearned = latest.total_words_learned;
    if (latest.longest_streak != null) base.statistics.longestStreak = latest.longest_streak;
    if (latest.total_tests_passed != null) base.statistics.totalTestsPassed = latest.total_tests_passed;

    if (latest.vocabulary_progress) base.vocabulary = latest.vocabulary_progress; // expects object keyed by category
    if (latest.grammar_progress) base.grammar = latest.grammar_progress; // expects object keyed by topic
    if (latest.achievements) base.achievements = latest.achievements; // expects array
    return base;
  }, [remote, JSON.stringify(userProgress)]);

  if (!isOpen) return null;

  const getText = (key) => {
    const texts = {
      overview: {
        traditional: '總覽',
        simplified: '总览',
        english: 'Overview'
      },
      vocabulary: {
        traditional: '詞彙',
        simplified: '词汇',
        english: 'Vocabulary'
      },
      grammar: {
        traditional: '語法',
        simplified: '语法',
        english: 'Grammar'
      },
      achievements: {
        traditional: '成就',
        simplified: '成就',
        english: 'Achievements'
      }
    };
    return texts[key]?.[language] || texts[key]?.english || key;
  };

  const renderOverview = () => (
    <div className="px-6 space-y-6">
      {/* Weekly Progress */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-6">
        <h3 className="text-lg font-bold mb-4">
          {language === 'traditional' ? '本週進度' :
           language === 'simplified' ? '本周进度' :
           'Weekly Progress'}
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold">{Math.floor((mergedProgress.statistics?.weeklyProgress || 0) / 60)}</div>
            <div className="text-sm opacity-90">
              {language === 'traditional' ? '小時' :
               language === 'simplified' ? '小时' :
               'Hours'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">{mergedProgress.statistics?.totalLessonsCompleted || 0}</div>
            <div className="text-sm opacity-90">
              {language === 'traditional' ? '完成課程' :
               language === 'simplified' ? '完成课程' :
               'Lessons'}
            </div>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-sm mb-2">
            <span>
              {language === 'traditional' ? '週目標' :
               language === 'simplified' ? '周目标' :
               'Weekly Goal'}
            </span>
            <span>{Math.floor((mergedProgress.statistics?.weeklyProgress || 0) / 60)} / {Math.floor((mergedProgress.statistics?.weeklyGoal || 300) / 60)} hrs</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-3">
            <div 
              className="bg-white rounded-full h-3 transition-all duration-500"
              style={{ 
                width: `${Math.min(100, ((mergedProgress.statistics?.weeklyProgress || 0) / (mergedProgress.statistics?.weeklyGoal || 300)) * 100)}%` 
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white/10 rounded-xl p-4 text-center">
          <TrendingUp className="w-8 h-8 mx-auto mb-2 text-green-400" />
          <div className="text-xl font-bold">{mergedProgress.statistics?.averageScore || 0}%</div>
          <div className="text-sm text-gray-300">
            {language === 'traditional' ? '平均分數' :
             language === 'simplified' ? '平均分数' :
             'Average Score'}
          </div>
        </div>
        <div className="bg-white/10 rounded-xl p-4 text-center">
          <Target className="w-8 h-8 mx-auto mb-2 text-blue-400" />
          <div className="text-xl font-bold">{mergedProgress.statistics?.totalWordsLearned || 0}</div>
          <div className="text-sm text-gray-300">
            {language === 'traditional' ? '學會單詞' :
             language === 'simplified' ? '学会单词' :
             'Words Learned'}
          </div>
        </div>
        <div className="bg-white/10 rounded-xl p-4 text-center">
          <Star className="w-8 h-8 mx-auto mb-2 text-yellow-400" />
          <div className="text-xl font-bold">{mergedProgress.statistics?.longestStreak || 0}</div>
          <div className="text-sm text-gray-300">
            {language === 'traditional' ? '最長連擊' :
             language === 'simplified' ? '最长连击' :
             'Longest Streak'}
          </div>
        </div>
        <div className="bg-white/10 rounded-xl p-4 text-center">
          <Trophy className="w-8 h-8 mx-auto mb-2 text-purple-400" />
          <div className="text-xl font-bold">{mergedProgress.statistics?.totalTestsPassed || 0}</div>
          <div className="text-sm text-gray-300">
            {language === 'traditional' ? '通過測試' :
             language === 'simplified' ? '通过测试' :
             'Tests Passed'}
          </div>
        </div>
      </div>
    </div>
  );

  const renderVocabulary = () => (
    <div className="px-6 space-y-4">
      {Object.entries(userProgress.vocabulary || {}).map(([category, progress]) => (
        <div key={category} className="bg-white/10 rounded-xl p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold capitalize">{category}</h3>
            <span className="text-sm text-gray-300">
              {progress.learned} / {progress.total}
            </span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${(progress.learned / progress.total) * 100}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-400">
            <span>
              {language === 'traditional' ? '已掌握' :
               language === 'simplified' ? '已掌握' :
               'Mastered'}: {progress.mastered}
            </span>
            <span>
              {language === 'traditional' ? '學習中' :
               language === 'simplified' ? '学习中' :
               'Studying'}: {progress.studying}
            </span>
          </div>
        </div>
      ))}
    </div>
  );

  const renderGrammar = () => (
    <div className="px-6 space-y-4">
      {Object.entries(userProgress.grammar || {}).map(([topic, progress]) => (
        <div key={topic} className="bg-white/10 rounded-xl p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold">{topic.replace('_', ' ')}</h3>
            <div className={`px-2 py-1 rounded text-xs ${
              progress.completed ? 'bg-green-500/20 text-green-300' : 'bg-gray-600/20 text-gray-400'
            }`}>
              {progress.completed ? 
                (language === 'traditional' ? '已完成' :
                 language === 'simplified' ? '已完成' :
                 'Completed') :
                (language === 'traditional' ? '進行中' :
                 language === 'simplified' ? '进行中' :
                 'In Progress')
              }
            </div>
          </div>
          {progress.completed && (
            <div className="flex justify-between text-sm">
              <span>
                {language === 'traditional' ? '分數' :
                 language === 'simplified' ? '分数' :
                 'Score'}: {progress.score}%
              </span>
              <span>
                {language === 'traditional' ? '嘗試次數' :
                 language === 'simplified' ? '尝试次数' :
                 'Attempts'}: {progress.attempts}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderAchievements = () => (
    <div className="px-6 space-y-4">
      {userProgress.achievements?.map((achievement) => (
        <div key={achievement.id} className="bg-white/10 rounded-xl p-4 flex items-center space-x-4">
          <div className="text-3xl">{achievement.icon}</div>
          <div className="flex-1">
            <h3 className="font-semibold">
              {language === 'traditional' ? achievement.name :
               language === 'simplified' ? achievement.nameSimplified :
               achievement.nameEnglish}
            </h3>
            <p className="text-gray-300 text-sm">
              {language === 'traditional' ? achievement.description :
               language === 'simplified' ? achievement.descriptionSimplified :
               achievement.descriptionEnglish}
            </p>
            <div className="flex items-center space-x-2 mt-2">
              <Star className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-400 text-sm">+{achievement.xpReward} XP</span>
            </div>
          </div>
        </div>
      )) || (
        <div className="text-center text-gray-400 py-8">
          {language === 'traditional' ? '尚無成就' :
           language === 'simplified' ? '尚无成就' :
           'No achievements yet'}
        </div>
      )}
    </div>
  );

  const tabs = ['overview', 'vocabulary', 'grammar', 'achievements'];

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white z-50 overflow-y-auto">
      <div className="flex justify-between items-center p-6">
        <h1 className="text-2xl font-bold">
          {language === 'traditional' ? '學習進度' :
           language === 'simplified' ? '学习进度' :
           'Learning Progress'}
        </h1>
        <button onClick={onClose}>
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Tabs */}
      <div className="px-6 mb-6">
        <div className="flex space-x-2 bg-gray-800/50 rounded-xl p-1">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              {getText(tab)}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="pb-8">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'vocabulary' && renderVocabulary()}
        {activeTab === 'grammar' && renderGrammar()}
        {activeTab === 'achievements' && renderAchievements()}
      </div>
    </div>
  );
};

export default ProgressTracker;
